/**
 * Color names based on https://www.color-name.com/hex/<PUT HEX CODE WITHOUT #>
 * If no comments: name in previous theme === name in webpage
 *
 https://www.color-name.com/hex/
 * If comment with a name only:
 *    Name in the comment is the webpage's name for the color
 *    Example: catskillWhite, mystic and gallery - Bright Gray
 *      Prev name was kept here, because the site name them all as Bright Gray
 *
 * If comment with a "prev ...": Using the name from the webpage.
 */
export const palette = {
  white: '#FFFFFF',
  concrete: '#F3F3F3', // Anti-Flash White
  platinum: '#E0E0E5', // Platinum prev MISCHKA
  chineseWhite: '#e0e0e0',
  catskillWhite: '#E6EAF3', // Bright Gray
  athensGray: '#F0F2F5', // Anti-Flash White
  aquaHaze: '#EFF3F6', // Anti-Flash White
  mystic: '#E6EBF1', // Bright Gray
  gallery: '#EEEEEE', // Bright Gray
  lightGray: '#D3D3D3', // prev ALTO
  lighterGray: '#E6E6E6',
  silverSand: '#C4C4C4', // prev SILVER
  quickSilver: '#A1A1A1', // prev SILVER_CHALICE
  gray: '#808080',
  sonicSilver: '#757575', // prev BOULDER
  midGray: '#66676C', // Old Lavender
  dimGray: '#696969', // prev DOVE_GRAY
  darkCharcoal: '#333333', // prev MINE_SHAFT
  blueCharcoal: '#010B22', // Rich Black
  darkJungleGreen: '#191C23', // prev SHARK
  black: '#000000',
  red: '#FF0000',
  orangeRed: '#FF4D4F',
  cadmiumRed: '#DF0423', // prev MONZA
  weldonBlue: '#7CA1AB', // prev GUMBO
  batteryChargedBlue: '#15B6E4', // prev JAVA
  coolBlack: '#002069',
  blueCola: '#008CDB', // prev CERULEAN
  tuftsBlue: '#309DD9', // prev CURIOUS_BLUE
  bondiBlue: '#0094BA',
  spaceCadet: '#143055', // prev BLUE_ZODIAC
  blueberry: '#448AFF', // prev DODGER_BLUE
  linkWater: '#EEEFFB', // Anti-Flash White
  northTexasGreen: '#10A028', // prev SALEM
  green: '#058105', // prev JAPANESE_LAUREL
  deepJungleGreen: '#07434B',
  mayGreen: '#45A145', // prev TOASTED_SALEM
  yellow: '#FFFF00',
  chineseYellow: '#FFAE00', // prev YELLOW_SEA
  orangeYellow: '#F5B324', // prev BUTTERCUP
  // New colors:
  desertSand: '#DBCFB0',
  oldSilver: '#858585',
  darkLiver: '#4E4E4E',
  darkGray: '#000000CF', // Black
  superLightGray: '#00000029', // Black
  richBlack: '#000029',
  erieBlack: '#1F1F1F',
  artichoke: '#988F78',
  gainsboro: '#DBDBDB',
  silverFoil: '#AFAFAF',
  graniteGray: '#646161',
  blackOlive: '#3C3A3A',
  raisinBlack: '#212121',
  meatBrown: '#E4BE42',
  outerSpace: '#2A3A40',
  guppieGreen: '#00FF80',
  blueJeans: '#58B0E0',
  metallicBlack: '#2C2C2C',
  lavenderBlush: '#FFF0F0',
  tuttiFruitti: '#C7313F',
} as const;

export const typography = {
  family: {
    arial: 'Arial',
    playfairDisplay: 'Playfair Display',
    helvetica: 'Helvetica, sans-serif',
  },
  size10: '10rem',
  size11: '11rem',
  size12: '12rem',
  size14: '14rem',
  size16: '16rem',
  size18: '18rem',
  size20: '20rem',
  size24: '24rem',
  size30: '30rem',
  size32: '32rem',
  size36: '36rem',
};

export const legacyDimensions = {
  pricingAreaWidth: '400rem',
  margin10px: '10rem',
  margin20px: '20rem',
  shapeTabsWidth: '290rem',
  cardsGap: '15rem',
  materialCardMaxWidth: '380rem',
  materialCardMinWidth: '280rem',
  colorCardMinWidth: '250rem',
  colorCardMaxWidth: '300rem',
  colorCardHeight: '140rem',
  imageHeight3d: '95rem',
  imageWidth3d: '180rem',
  cardSingleSelectWidth: '210rem',
  cardSingleSelectHeight: '160rem',
  imageHeight: '95rem',
  imageWidth: '180rem',
  largeCardWidth: '270rem',
  largeCardImageWidth: '180rem',
  largeCardImageHeight: '95rem',
  cardWithImageWidth: '210rem',
  cardWithImageHeight: '180rem',
  imageHeightAppliance: '95rem',
  imageWidthAppliance: '145rem',
  cardWithApplianceWidth: '210rem',
  cardWithApplianceHeight: '160rem',
  subtitleMargin: '10rem',
  eeScrollbarWidth: '15rem',
};

const bpWidth = {
  xs: 480,
  sm: 768,
  md: 1080,
  lg: 1280,
  xl: 1920,
} as const;

const getMediaMin = (bp: keyof typeof bpWidth) =>
  `@media (min-width: ${bpWidth[bp]}px)`;

// Mobile first approach
const getMediaMinMF = (bp: keyof typeof bpWidth) =>
  `@media (min-width: ${bpWidth[bp] + 1}px)`;

const getMediaMax = (bp: keyof typeof bpWidth) =>
  `@media (max-width: ${bpWidth[bp]}px)`;

export const breakpoint = {
  screen: {
    /**
     * @see https://screensiz.es/
     * @see https://tailwindcss.com/docs/responsive-design/
     */
    xs: `${bpWidth.xs}rem`,
    sm: `${bpWidth.sm}rem`,
    md: `${bpWidth.md}rem`,
    lg: `${bpWidth.lg}rem`,
    xl: `${bpWidth.xl}rem`,
  },
  media: {
    min: {
      xs: getMediaMin('xs'),
      sm: getMediaMin('sm'),
      md: getMediaMin('md'),
      lg: getMediaMin('lg'),
      xl: getMediaMin('xl'),
    },
    max: {
      xs: getMediaMax('xs'),
      sm: getMediaMax('sm'),
      md: getMediaMax('md'),
      lg: getMediaMax('lg'),
      xl: getMediaMax('xl'),
    },
    minMf: {
      xs: getMediaMinMF('xs'),
      sm: getMediaMinMF('sm'),
      md: getMediaMinMF('md'),
      lg: getMediaMinMF('lg'),
      xl: getMediaMinMF('xl'),
    },
  },
  prose: '65ch',
} as const;

export const overlayPosition = {
  layer0: 0,
  layer1: 1,
  layer10: 10,
  layerImportant: 999,
  layerNegative: -1,
} as const;
